import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';

// Canonical infinite-list hook (frontend-conventions §7.5). Wraps TanStack v5
// useInfiniteQuery for any cursor-paginated list returning the FROZEN envelope
// `{ items, nextCursor }` (no `total`). Applies to ALL lists that can exceed
// ~400 rows (cases, passengers, hotels, reservations, audit).
//
// Contract:
//   - opaque cursor: `getNextPageParam: last => last.nextCursor ?? undefined`
//     (undefined stops the walk — TanStack treats it as "no more pages").
//   - `initialPageParam: undefined` → first page sends no cursor.
//   - `queryKey: [...baseKey, filters]` → filter changes start a fresh walk.
//   - `placeholderData: keepPreviousData` → a filter change (a typed query in
//     Express, an airport chip on the board) KEEPS the rows on screen while the
//     new walk loads instead of blanking to a skeleton. The list reads as
//     narrowing rather than reloading; `isStale` says the rows belong to the
//     previous filter so the caller can dim them.
//   - returns flattened `items` plus the fetch-more affordances the sentinel
//     drives.

/** The frozen page envelope every paginated endpoint returns. */
export interface IPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface IUseInfiniteListArgs<TItem, TFilters> {
  /** Stable key prefix for this list (e.g. `['cases']`). */
  baseKey: QueryKey;
  /** Current filter object — part of the query key, so changes refetch page 1. */
  filters: TFilters;
  /** Fetch one page given the active filters + an opaque cursor (undefined = first). */
  queryFn: (filters: TFilters, cursor: string | undefined) => Promise<IPage<TItem>>;
  /** Optional per-call staleTime override (defaults to the client default). */
  staleTime?: number;
  /** Gate the query (e.g. a tab/dialog that isn't open). Defaults to enabled. */
  enabled?: boolean;
  /**
   * Optional background poll interval (ms) for live lists that have no WS push
   * yet (frontend-conventions §15 prefers WS; polling is the M1 stopgap). When
   * set, TanStack refetches the already-loaded pages on this cadence — it does
   * NOT walk forward; `fetchNextPage` still drives pagination.
   */
  refetchInterval?: number;
}

export interface IUseInfiniteListResult<TItem> {
  /** All loaded pages flattened, newest-first as the server ordered them. */
  items: TItem[];
  isLoading: boolean;
  isError: boolean;
  /**
   * The rows on screen are the PREVIOUS filter's while this one loads (see
   * `keepPreviousData`). Callers use it for a subtle "narrowing" affordance —
   * never to hide the rows, which is the skeleton flash this replaces.
   */
  isStale: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Refetch this specific query (only this filter's observer — not siblings). */
  refetch: () => void;
  /**
   * The FIRST page's raw envelope, untyped beyond the frozen shape — for
   * endpoints that ride additive metadata on the envelope (e.g. the flights
   * monitor's board `summary`). Callers narrow it themselves; `undefined`
   * until the first page lands.
   */
  firstPage: IPage<TItem> | undefined;
}

export function useInfiniteList<TItem, TFilters>(
  args: IUseInfiniteListArgs<TItem, TFilters>,
): IUseInfiniteListResult<TItem> {
  const query = useInfiniteQuery<IPage<TItem>>({
    queryKey: [...(args.baseKey as unknown[]), args.filters] as QueryKey,
    queryFn: ({ pageParam }) =>
      args.queryFn(args.filters, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    ...(args.staleTime !== undefined ? { staleTime: args.staleTime } : {}),
    ...(args.enabled !== undefined ? { enabled: args.enabled } : {}),
    ...(args.refetchInterval !== undefined ? { refetchInterval: args.refetchInterval } : {}),
  });

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    isStale: query.isPlaceholderData,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    refetch: () => {
      void query.refetch();
    },
    firstPage: query.data?.pages[0],
  };
}
